import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import { Linkedin, Instagram, Send, Mail, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import AnimatedSection from "@/components/AnimatedSection";

const Contact = () => {
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    jobTitle: "",
    message: "",
    consent: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.consent) {
      toast({
        title: "Consentement requis",
        description: "Veuillez accepter d'être contacté par GEO Agency.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke('submit-contact', {
        body: {
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || null,
          company: formData.company.trim(),
          job_title: formData.jobTitle.trim() || null,
          message: formData.message.trim(),
          consent: formData.consent,
        },
      });

      if (error) throw error;

      if (data?.error) {
        const errorMessage = data.details?.join(', ') || data.error;
        throw new Error(errorMessage);
      }

      toast({
        title: "Demande envoyée !",
        description:
          "Nous avons bien reçu votre demande d'audit. Notre équipe vous contactera sous 24h.",
      });
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        company: "",
        jobTitle: "",
        message: "",
        consent: false,
      });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast({
        title: "Erreur",
        description: error instanceof Error ? error.message : "Une erreur s'est produite. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="pt-4 lg:pt-6 pb-12 lg:pb-16 bg-slate-50/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-lg">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/30 via-transparent to-sky-50/30" />
            
            <div className="relative z-10 p-6 lg:p-8">
              {/* Header compact */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 border border-emerald-200 mb-3">
                  <Sparkles className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">
                    {language === "fr" ? "Audit gratuit" : "Free audit"}
                  </span>
                </div>
                
                <h2 className="text-2xl lg:text-3xl font-display font-bold text-slate-800 mb-2">
                  {language === "fr" ? "Obtenez votre " : "Get your "}
                  <span className="bg-gradient-to-r from-emerald-400 via-sky-500 to-emerald-400 bg-clip-text text-transparent animate-gradient-shine">
                    {language === "fr" ? "audit GEO gratuit" : "free GEO audit"}
                  </span>
                </h2>
                <p className="text-sm text-slate-500">{t("contact.subtitle")}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="firstName" className="text-slate-700 text-sm">
                      {t("contact.firstname")} <span className="text-emerald-500">*</span>
                    </Label>
                    <Input
                      id="firstName"
                      required
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-emerald-400 focus:ring-emerald-400/20 h-10"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lastName" className="text-slate-700 text-sm">
                      {t("contact.name")} <span className="text-emerald-500">*</span>
                    </Label>
                    <Input
                      id="lastName"
                      required
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className="bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-emerald-400 focus:ring-emerald-400/20 h-10"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-slate-700 text-sm">
                      {t("contact.email")} <span className="text-emerald-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-emerald-400 focus:ring-emerald-400/20 h-10"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="company" className="text-slate-700 text-sm">
                      {t("contact.company")} <span className="text-emerald-500">*</span>
                    </Label>
                    <Input
                      id="company"
                      required
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-emerald-400 focus:ring-emerald-400/20 h-10"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="message" className="text-slate-700 text-sm">
                    {t("contact.message")} <span className="text-emerald-500">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    required
                    rows={3}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="bg-white border-slate-200 text-slate-800 placeholder:text-slate-400 focus:border-emerald-400 focus:ring-emerald-400/20 resize-none"
                    placeholder={t("contact.message.placeholder")}
                  />
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="consent"
                    checked={formData.consent}
                    onCheckedChange={(checked) => setFormData({ ...formData, consent: checked as boolean })}
                    className="border-slate-300 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500 mt-0.5"
                  />
                  <Label htmlFor="consent" className="text-xs text-slate-500 cursor-pointer leading-relaxed">
                    {t("contact.rgpd")}
                  </Label>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-emerald-500 to-sky-500 hover:from-emerald-400 hover:to-sky-400 text-white font-semibold py-5 rounded-full shadow-xl shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-300 hover:scale-[1.02] group"
                >
                  {isSubmitting ? (
                    <span className="animate-pulse">...</span>
                  ) : (
                    <>
                      {t("contact.submit")}
                      <Send className="ml-2 h-5 w-5 group-hover:translate-x-2 group-hover:-translate-y-1 transition-transform duration-300" />
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-4 border-t border-slate-200 flex flex-wrap items-center justify-center gap-4 text-sm">
                <a href="mailto:eliott@gogenia.ai" className="text-slate-500 hover:text-emerald-600 transition-colors flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  eliott@gogenia.ai
                </a>
                <a href="https://www.linkedin.com/company/gogenia/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 hover:text-sky-500 hover:border-sky-300 transition-all">
                  <Linkedin className="h-4 w-4" />
                </a>
                <a href="https://www.instagram.com/gogenia.ai" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500 hover:text-pink-500 hover:border-pink-300 transition-all">
                  <Instagram className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
